import React, { useEffect, useRef, useState } from "react";
import s from "./CountUp.module.scss";

// Counts the leading number of a value up from 0 once it scrolls into view:
// "4,256 km" -> 0 km ... 4,256 km. Values without a leading number
// (e.g. "PPP · B2B · B2G") are shown as is.
const CountUp = ({ value, duration = 2600, step: customStep }) => {
  const match = value.match(/^([\d,]+)(.*)$/);
  const target = match ? parseInt(match[1].replace(/,/g, ""), 10) : 0;
  const suffix = match ? match[2] : "";

  const ref = useRef(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!match) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setCurrent(target);
      return;
    }

    let frame;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        // bigger numbers move in round steps (4,256 -> by 500, 508 -> by 10)
        // so the digits don't flicker; the screen updates ~16 times a second
        const step =
          customStep || (target >= 1000 ? 500 : target >= 100 ? 10 : 1);
        const start = performance.now();
        let last = 0;
        const tick = (now) => {
          const t = Math.min((now - start) / duration, 1);
          if (t === 1) {
            setCurrent(target);
            return;
          }
          if (now - last >= 60) {
            last = now;
            // constant speed, no slowdown at the end
            setCurrent(Math.floor((target * t) / step) * step);
          }
          frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [target, duration, customStep]);

  if (!match) return <span>{value}</span>;

  return (
    <span ref={ref} className={s.number}>
      {current.toLocaleString("en-US")}
      {suffix}
    </span>
  );
};

export default CountUp;
